/**
 * Stock Management Service
 * Handles automatic stock updates based on document operations
 */

import i18n from '../../i18n';
import { productService } from '../../shared/utils/serviceProvider';
import stockMovementsStorage from './stockMovementsStorage';

const sm = (key, opts) => i18n.t(`serviceMessages.stockManager.${key}`, opts);

const STOCK_OPERATION_KEYS = {
  INCREASE: {
    purchase_order: 'purchase_order',
    reception: 'reception',
    purchase: 'purchase',
    return_from_client: 'return_from_client',
  },
  DECREASE: {
    invoice: 'invoice',
    delivery_note: 'delivery_note',
    sale: 'sale',
    return_to_supplier: 'return_to_supplier',
  },
};

const buildStockOperations = () => ({
  INCREASE: Object.fromEntries(
    Object.entries(STOCK_OPERATION_KEYS.INCREASE).map(([key, labelKey]) => [key, sm(labelKey)])
  ),
  DECREASE: Object.fromEntries(
    Object.entries(STOCK_OPERATION_KEYS.DECREASE).map(([key, labelKey]) => [key, sm(labelKey)])
  ),
});

export let STOCK_OPERATIONS = buildStockOperations();

/**
 * Check if a document type affects stock
 * @param {string} documentType - The document type
 * @returns {Object|null} - Operation info or null if no stock impact
 */
export const getStockOperation = (documentType) => {
  if (Object.keys(STOCK_OPERATION_KEYS.INCREASE).includes(documentType)) {
    return {
      type: 'INCREASE',
      operation: sm(STOCK_OPERATION_KEYS.INCREASE[documentType]),
    };
  }

  if (Object.keys(STOCK_OPERATION_KEYS.DECREASE).includes(documentType)) {
    return {
      type: 'DECREASE',
      operation: sm(STOCK_OPERATION_KEYS.DECREASE[documentType]),
    };
  }

  return null;
};

/**
 * Update stock for products in a document
 * @param {Object} document - The document with items
 * @param {Array} products - Available products to get current stock
 * @returns {Promise<Array>} - Array of stock movements made
 */
export const updateStockFromDocument = async (document, products = []) => {
  console.log('?? Stock Manager - Processing document:', {
    type: document.type,
    number: document.number,
    itemCount: document.items?.length || 0
  });

  const operation = getStockOperation(document.type);
  if (!operation) {
    console.log('?? No stock operation needed for document type:', document.type);
    return [];
  }

  if (!document.items || !Array.isArray(document.items) || document.items.length === 0) {
    console.log('?? No items to process in document');
    return [];
  }

  const stockMovements = [];

  for (const item of document.items) {
    try {
      // Skip items without product reference or quantity
      if (!item.product || !item.quantity || item.quantity <= 0) {
        console.log('?? Skipping item - missing product or quantity:', item);
        continue;
      }

      // Find the product in the products list
      const product = products.find(p => p.id === item.product);
      if (!product) {
        console.log('?? Product not found:', item.product);
        continue;
      }

      // Skip services (they don't have stock)
      if (product.type === 'service') {
        console.log('?? Skipping service item:', product.name);
        continue;
      }

      const currentStock = parseInt(product.stock || 0);
      const quantityChange = parseInt(item.quantity);
      let newStock;

      if (operation.type === 'INCREASE') {
        newStock = currentStock + quantityChange;
        console.log(`?? STOCK INCREASE: ${product.name} - ${currentStock} + ${quantityChange} = ${newStock}`);
      } else {
        newStock = Math.max(0, currentStock - quantityChange); // Don't allow negative stock
        console.log(`?? STOCK DECREASE: ${product.name} - ${currentStock} - ${quantityChange} = ${newStock}`);

        // Warn if stock would go negative
        if (currentStock - quantityChange < 0) {
          console.warn(`?? WARNING: Stock would go negative for ${product.name}. Setting to 0.`);
        }
      }

      // Update product stock via service
      await productService.updateProduct(product.id, {
        ...product,
        stock: newStock
      });

      // Record the movement
      const movement = {
        id: Date.now() + Math.random(),
        productId: product.id,
        productName: product.name,
        documentId: document.id,
        documentType: document.type,
        documentNumber: document.number,
        operation: operation.operation,
        operationType: operation.type,
        quantityChange: operation.type === 'INCREASE' ? quantityChange : -quantityChange,
        previousStock: currentStock,
        newStock: newStock,
        timestamp: new Date().toISOString()
      };

      stockMovements.push(movement);
      stockMovementsStorage.addStockMovement(movement);

    } catch (error) {
      console.error('?? Error processing stock for item:', item, error);
    }
  }

  console.log(`?? Stock update complete: ${stockMovements.length} movements recorded`);
  return stockMovements;
};

/**
 * Reverse stock changes from a document (for cancellation/deletion)
 * @param {Object} document - The document to reverse
 * @param {Array} products - Available products
 * @returns {Promise<Array>} - Array of reversal movements
 */
export const reverseStockFromDocument = async (document, products = []) => {
  console.log('?? Reversing stock for document:', document.type, document.number);

  const operation = getStockOperation(document.type);
  if (!operation) return [];

  // Create a reversed document for processing
  const reversedDocument = {
    ...document,
    type: operation.type === 'INCREASE' ? 'reverse_increase' : 'reverse_decrease'
  };

  // Temporarily modify the operation mapping for reversal
  const originalIncrease = { ...STOCK_OPERATIONS.INCREASE };
  const originalDecrease = { ...STOCK_OPERATIONS.DECREASE };

  if (operation.type === 'INCREASE') {
    STOCK_OPERATIONS.DECREASE.reverse_increase = sm('cancellation', { operation: operation.operation });
  } else {
    STOCK_OPERATIONS.INCREASE.reverse_decrease = sm('cancellation', { operation: operation.operation });
  }

  try {
    const movements = await updateStockFromDocument(reversedDocument, products);
    return movements;
  } finally {
    // Restore original mappings
    STOCK_OPERATIONS.INCREASE = originalIncrease;
    STOCK_OPERATIONS.DECREASE = originalDecrease;
  }
};

/**
 * Get stock movement history for a product
 * @param {string} productId - Product ID
 * @param {Array} allMovements - All recorded movements
 * @returns {Array} - Movements for the specific product
 */
export const getProductStockHistory = (productId, allMovements = []) => {
  return allMovements
    .filter(movement => movement.productId === productId)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

/**
 * Calculate expected stock based on pending documents
 * @param {Object} product - Product object
 * @param {Array} pendingDocuments - Documents that haven't been processed yet
 * @returns {Object} - Stock projection
 */
export const calculateStockProjection = (product, pendingDocuments = []) => {
  let projectedStock = parseInt(product.stock || 0);
  const projectedMovements = [];

  for (const doc of pendingDocuments) {
    const operation = getStockOperation(doc.type);
    if (!operation || !doc.items) continue;

    for (const item of doc.items) {
      if (item.product === product.id && item.quantity > 0) {
        const change = parseInt(item.quantity);
        const oldProjected = projectedStock;

        if (operation.type === 'INCREASE') {
          projectedStock += change;
        } else {
          projectedStock = Math.max(0, projectedStock - change);
        }

        projectedMovements.push({
          document: `${doc.type} ${doc.number}`,
          operation: operation.operation,
          change: operation.type === 'INCREASE' ? `+${change}` : `-${change}`,
          projectedStock
        });
      }
    }
  }

  return {
    currentStock: parseInt(product.stock || 0),
    projectedStock,
    movements: projectedMovements,
    difference: projectedStock - parseInt(product.stock || 0)
  };
};

export default {
  getStockOperation,
  updateStockFromDocument,
  reverseStockFromDocument,
  getProductStockHistory,
  calculateStockProjection,
  STOCK_OPERATIONS
};
